// tabs/OverviewTab.js
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { API_BASE_URL } from 'config/api';
import {
  SectionCard,
  SectionTitle,
  DataCard,
  InfoRow,
  InfoLabel,
  InfoValue,
  StatusBadge,
  RoleBadge,
  honeyColors,
} from '../styled';

dayjs.extend(relativeTime);

const StatCard = ({ title, value, subtitle, icon, color = honeyColors.primary, trend }) => (
  <Card
    sx={{
      backgroundColor: honeyColors.surface,
      borderRadius: '16px',
      border: `1px solid ${honeyColors.border}`,
      height: '100%',
    }}
  >
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: honeyColors.textPrimary }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <TrendingUpIcon sx={{ fontSize: 16, color: honeyColors.success }} />
              <Typography variant="body2" sx={{ color: honeyColors.success }}>
                {trend}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}20`,
            borderRadius: '12px',
            padding: '12px',
          }}
        >
          {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const OverviewTab = ({ community, stats, members, venues, upcomingEvents }) => {
  const getImageUrl = (image) => {
    if (!image) return null;
    return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
  };

  const getRoleText = (role) => {
    const map = { 'FO': 'Founder', 'AD': 'Admin', 'MA': 'Manager', 'PT': 'Partner', 'EO': 'Event Organizer', 'PA': 'Member', 'GU': 'Guest' };
    return map[role] || role;
  };

  const getStatusText = (status) => {
    const map = { 'AC': 'Active', 'PP': 'Pending', 'IN': 'Inactive' };
    return map[status] || status;
  };

  // Recent members (last 5 active)
  const recentMembers = members
    .filter(m => m.status === 'AC')
    .slice(0, 5);

  // Pending members
  const pendingMembers = members.filter(m => m.status === 'PP');

  // Member growth (mock calculation)
  const memberGrowth = stats.totalMembers > 0 ? `+${Math.round(stats.totalMembers * 0.12)} this month` : null;

  return (
    <Box>
      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Members"
            value={stats.totalMembers}
            subtitle={`${stats.activeMembers} active`}
            icon={<PeopleIcon />}
            color={honeyColors.primary}
            trend={memberGrowth}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Approvals"
            value={stats.pendingMembers}
            subtitle="Awaiting review"
            icon={<ScheduleIcon />}
            color={stats.pendingMembers > 0 ? honeyColors.warning : honeyColors.success}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Partner Venues"
            value={stats.totalVenues}
            subtitle="Active locations"
            icon={<StoreIcon />}
            color={honeyColors.info}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Upcoming Events"
            value={stats.upcomingEvents}
            subtitle="Scheduled"
            icon={<EventIcon />}
            color={honeyColors.success}
          />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Pending Approvals Section */}
          {pendingMembers.length > 0 && (
            <SectionCard sx={{ mb: 3 }}>
              <CardContent>
                <SectionTitle>
                  <WarningIcon />
                  Pending Member Approvals ({pendingMembers.length})
                </SectionTitle>
                <List>
                  {pendingMembers.slice(0, 5).map((member, index) => (
                    <React.Fragment key={member.slug || index}>
                      <ListItem
                        sx={{
                          backgroundColor: honeyColors.surfaceHoney,
                          borderRadius: '12px',
                          mb: 1,
                        }}
                        secondaryAction={
                          <Box display="flex" gap={1}>
                            <Chip
                              label="Review"
                              size="small"
                              sx={{
                                backgroundColor: honeyColors.primary,
                                color: honeyColors.textPrimary,
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar src={getImageUrl(member.user?.avatar)}>
                            {member.user?.first_name?.charAt(0) || '?'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${member.user?.first_name || ''} ${member.user?.last_name || ''}`}
                          secondary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" color="textSecondary">
                                {member.user?.email}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                • Applied {dayjs(member.create_date).fromNow()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
                {pendingMembers.length > 5 && (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ textAlign: 'center', mt: 2, cursor: 'pointer' }}
                  >
                    View all {pendingMembers.length} pending members →
                  </Typography>
                )}
              </CardContent>
            </SectionCard>
          )}

          {/* Recent Members */}
          <SectionCard sx={{ mb: 3 }}>
            <CardContent>
              <SectionTitle>
                <PeopleIcon />
                Recent Members
              </SectionTitle>
              <List>
                {recentMembers.length > 0 ? (
                  recentMembers.map((member, index) => (
                    <ListItem
                      key={member.slug || index}
                      sx={{
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: honeyColors.surfaceHoney },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar src={getImageUrl(member.user?.avatar)}>
                          {member.user?.first_name?.charAt(0) || '?'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${member.user?.first_name || ''} ${member.user?.last_name || ''}`}
                        secondary={member.user?.email}
                      />
                      <RoleBadge role={member.role} label={getRoleText(member.role)} size="small" />
                    </ListItem>
                  ))
                ) : (
                  <Typography color="textSecondary" textAlign="center" py={4}>
                    No active members yet
                  </Typography>
                )}
              </List>
            </CardContent>
          </SectionCard>

          {/* Upcoming Events */}
          <SectionCard>
            <CardContent>
              <SectionTitle>
                <EventIcon />
                Upcoming Events
              </SectionTitle>
              {upcomingEvents.length > 0 ? (
                <List>
                  {upcomingEvents.slice(0, 5).map((event, index) => (
                    <ListItem
                      key={event.slug || index}
                      sx={{
                        borderRadius: '8px',
                        border: `1px solid ${honeyColors.border}`,
                        mb: 1,
                        '&:hover': { borderColor: honeyColors.primary },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={getImageUrl(event.image)}
                          sx={{ width: 56, height: 56, mr: 2 }}
                        >
                          <EventIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={event.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {dayjs(event.start).format('ddd, MMM D, YYYY • h:mm A')}
                            </Typography>
                            {event.venue?.name && (
                              <Typography variant="body2" color="textSecondary">
                                @ {event.venue.name}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary" textAlign="center" py={4}>
                  No upcoming events
                </Typography>
              )}
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Community Info */}
          <SectionCard sx={{ mb: 3 }}>
            <CardContent>
              <SectionTitle>Community Details</SectionTitle>
              <Box>
                <InfoRow>
                  <InfoLabel>Slug</InfoLabel>
                  <InfoValue sx={{ fontFamily: 'monospace' }}>{community.slug}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Created</InfoLabel>
                  <InfoValue>{dayjs(community.create_date).format('MMM D, YYYY')}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Contact Email</InfoLabel>
                  <InfoValue>{community.contact_email || 'Not set'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Space Limit</InfoLabel>
                  <InfoValue>{community.space_limit || 'Unlimited'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Admission Questions</InfoLabel>
                  <InfoValue>
                    {community.requires_admission_questions ? (
                      <Chip label="Required" size="small" color="primary" />
                    ) : (
                      <Chip label="Not Required" size="small" variant="outlined" />
                    )}
                  </InfoValue>
                </InfoRow>
              </Box>
            </CardContent>
          </SectionCard>

          {/* Member Breakdown */}
          <SectionCard sx={{ mb: 3 }}>
            <CardContent>
              <SectionTitle>Member Breakdown</SectionTitle>
              <Box>
                {['FO', 'AD', 'MA', 'PT', 'EO', 'PA', 'GU'].map(role => {
                  const count = members.filter(m => m.role === role && m.status === 'AC').length;
                  const percentage = stats.activeMembers > 0 ? (count / stats.activeMembers) * 100 : 0;
                  
                  return (
                    <Box key={role} sx={{ mb: 2 }}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="textSecondary">
                          {getRoleText(role)}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: honeyColors.border,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: honeyColors.primary,
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </SectionCard>

          {/* Partner Venues */}
          <SectionCard>
            <CardContent>
              <SectionTitle>
                <StoreIcon />
                Partner Venues
              </SectionTitle>
              {venues.length > 0 ? (
                <List>
                  {venues.slice(0, 5).map((venue, index) => (
                    <ListItem
                      key={venue.slug || index}
                      sx={{
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: honeyColors.surfaceHoney },
                        px: 0,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={getImageUrl(venue.image)}
                          sx={{ width: 48, height: 48 }}
                        >
                          <StoreIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={venue.name}
                        secondary={venue.single_address}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary" textAlign="center" py={4}>
                  No partner venues yet
                </Typography>
              )}
            </CardContent>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewTab;
